import React from 'react';
import { IMaskInput } from 'react-imask';
import EditIcon from '@mui/icons-material/Edit';
import { Controller, useForm } from 'react-hook-form';
import { IUserChangePhoneForm } from '@src/features/profile/entites';
import { EMPTY_STRING, EMPTY_VALUE, REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { ArcButton, ArcCellInfo, ArcDialog, ArcIconButton, ArcOutlinedInput, ArcStack, ArcText } from '@src/core/ui';
import { ChangePhoneFormProps, CustomChangeEvent, CustomMaskedInputProps } from './types';
import { useDisclosure } from '@src/core/hooks';
import { useUpdateProfileMutation } from '@src/features/profile/redux';
import { toast } from 'react-toastify';
import { trackError, toTrackedError } from '@src/core/sentry';

const ChangePhoneForm: React.FC<ChangePhoneFormProps> = (props) => {
  const { profile } = props;

  const { open, show, hide } = useDisclosure();
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  const { control, formState: { errors }, handleSubmit } = useForm<IUserChangePhoneForm>({
    defaultValues: {
      phone: profile?.phone ?? EMPTY_VALUE,
    }
  });

  const onUpdateProfile = (form: IUserChangePhoneForm) => {
    updateProfile({ 
      phone: form.phone, 
      lastname: profile?.lastname ?? EMPTY_VALUE, 
      firstname: profile?.firstname ?? EMPTY_VALUE, 
    }).unwrap().then(() => {
      toast.success('UPDATE PROFILE SUCCESS TEXT');
      hide();
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0019', extra: { action: 'updatePhone' } });
      toast.error('Failed to update phone');
    });
  };

  return (
    <React.Fragment>
      <ArcCellInfo label='Phone'>
        <ArcStack 
          spacing={1}
          direction='row' 
          alignItems='center'>
          <ArcText.h6 height={16}>
            {profile?.phone ?? EMPTY_STRING} 
          </ArcText.h6>
          <ArcIconButton size='small' onClick={show}>
            <EditIcon sx={{ fontSize: 16 }} />
          </ArcIconButton>
        </ArcStack>
        
      </ArcCellInfo>
      <ArcDialog open={open} onClose={hide} title='Change Phone'>
        <ArcStack p={2} spacing={2} component='form' onSubmit={handleSubmit(onUpdateProfile)}>
          <Controller 
            name='phone' 
            control={control} 
            rules={{ required: REQUIRED_VALIDATION_TEXT }}
            render={({ field }) => (
              <ArcOutlinedInput 
                {...field} 
                label='Phone'
                autoComplete='off'
                error={Boolean(errors.phone)} 
                helperText={errors.phone?.message} />
            )}/>

          <ArcButton.brand 
            type='submit' 
            isLoading={isLoading}> 
            Save 
          </ArcButton.brand>
        </ArcStack>
      </ArcDialog>
    </React.Fragment>
  );
};


const MaskedInput = React.forwardRef<HTMLInputElement, CustomMaskedInputProps>(
  ({ onChange, name, mask, ...other }, ref) => {
    return (
      <IMaskInput
        {...other}
        inputRef={ref}
        mask={mask ?? EMPTY_VALUE}
        onComplete={(_, maskRef) => {
          const event = {
            target: {
              name: name || '',
              value: maskRef.unmaskedValue,
            },
          };

          if (onChange) {
            (onChange as unknown as (event: CustomChangeEvent) => void)(event);
          }
        }}
      />
    );
  },
);

MaskedInput.displayName = 'MaskedInput';


export default ChangePhoneForm;